import { useEffect, useState } from 'react';
import { usePostLoanAdd } from '@repo/api/endpoints/loan';
import type { LoanRequestBodyType } from '@repo/api/endpoints/loan';
import type { LoanProviderRequest } from '@repo/shared/types';
import type { MerchantResponse } from '@repo/api/endpoints/merchant';
import { isAnyLoanFinished } from '@repo/store/helpers';
import {
  Limit,
  LimitMerchant,
  OfferLimit,
  loanProviderRequest,
  promotionCodeErrorType,
} from '@repo/shared/types';
import { isFetcherError } from '@repo/api/client';
import { loanAdd422ResponseSchema } from '@repo/api/endpoints/loan';

import { ApplicationSuspended } from '@/components/loan-process/loan-stages/shared/application-suspended/application-suspended';
import { ApplicationProcessing } from '@/components/loan-process/loan-stages/stage-0/application-processing/application-processing';
import { useLoanProcessStore } from '@/components/loan-process/store/loan-process.store';
import { useLoanProcessModal } from '@/components/loan-process/hooks/use-loan-process-modal';
import { BlockNewApplicationSubmit } from '@/components/loan-process/loan-stages/stage-0/block-new-application-submit/block-new-application-submit';
import {
  clearDiscountStorage,
  deleteDiscountForPeriod,
} from '@/components/promotion-code/promotion-code-storage';
import { usePromotionCodeCallback } from '@/components/promotion-code/use-promotion-code-callback';

interface StageZero {
  merchantResponseData: MerchantResponse | undefined;
  applicationFormData?: LoanRequestBodyType;
  loanLimit: Limit | OfferLimit | LimitMerchant | undefined;
}

// Source selection popup (HowDoYouKnowAboutP24Financing) has been disabled — we send a fixed
// source instead. The component is kept in the codebase for potential future reuse.
const DEFAULT_LOAN_SOURCE = 'Panel transakcyjny Przelewy24';
export function StageZero({ applicationFormData, merchantResponseData, loanLimit }: StageZero) {
  const [providerRequest, setProviderRequest] = useState<undefined | LoanProviderRequest>();
  const [discountErrorHandled, setDiscountErrorHandled] = useState(false);
  const { mutate: addLoanMutate, status: addLoanStatus } = usePostLoanAdd();

  const { setLoanStage } = useLoanProcessModal();

  const {
    actions: { setLoanData, clearLoanProcessStore },
    state: { onDiscountApplied, onDiscountCleared },
  } = useLoanProcessStore();

  const { handlePromotionCodeError } = usePromotionCodeCallback({
    onDiscountApplied,
    onDiscountCleared,
  });

  const isProviderRequestFailed = (provider: undefined | LoanProviderRequest) =>
    provider === loanProviderRequest.enum.requestFailed;

  const isMutateRequestEnable = applicationFormData;

  const source = isAnyLoanFinished(merchantResponseData?.data) ? 'Next loan' : DEFAULT_LOAN_SOURCE;

  const isProviderBlocked = !merchantResponseData?.config.find(
    (providerConfig) => providerConfig.provider === applicationFormData?.data.loan.provider,
  )?.active;

  useEffect(() => {
    if (isMutateRequestEnable) {
      addLoanMutate(
        {
          ...applicationFormData,
          data: {
            ...applicationFormData.data,
            source,
            limitId: loanLimit?.id || applicationFormData.data.limitId,
          },
        },
        {
          onSuccess: ({ data }) => {
            if (isProviderRequestFailed(data.providerRequest)) {
              setProviderRequest(data.providerRequest);
              clearDiscountStorage();
              onDiscountCleared?.();

              return;
            }
            clearDiscountStorage();
            setLoanStage(1);
            setLoanData({
              amount: applicationFormData.data.loan.amount,
              id: data.loanId,
              period: applicationFormData.data.loan.period,
              pricing: applicationFormData.data.loan.pricing,
              loanRepaymentPrice: applicationFormData.data.loan.loanRepaymentPrice,
              documents: [],
              loanAmountChange: false,
              provider: applicationFormData.data.loan.provider,
            });
          },
          onError: (error) => {
            if (isFetcherError(error) && error.status === 422) {
              const parsed = loanAdd422ResponseSchema.safeParse(error.data);
              if (parsed.success) {
                const reasonCodeParse = promotionCodeErrorType.safeParse(
                  parsed.data.data.reasonCode,
                );
                if (reasonCodeParse.success) {
                  const { availableRevenueRate } = parsed.data.data;

                  if (availableRevenueRate) {
                    // 422 with availableRevenueRate (INVALID_OFFER, LOAN_AMOUNT_TO_HIGH, LOAN_AMOUNT_TO_LOW, PRICING_TOO_LOW)
                    const { reasonCode } = parsed.data.data;
                    const { discounted } = availableRevenueRate;
                    let amount: number | undefined;
                    if (reasonCode === 'LOAN_AMOUNT_TO_HIGH') {
                      amount = discounted.amountMax ?? undefined;
                    } else if (reasonCode === 'LOAN_AMOUNT_TO_LOW') {
                      amount = discounted.amountMin ?? undefined;
                    }
                    const submittedPeriod = applicationFormData?.data.loan.period;
                    const newPeriod = availableRevenueRate.original.period;
                    const userId = merchantResponseData?.data.merchantData.user.userId;
                    if (
                      submittedPeriod !== undefined &&
                      submittedPeriod !== newPeriod &&
                      userId !== undefined
                    ) {
                      deleteDiscountForPeriod(userId, submittedPeriod);
                    }
                    setDiscountErrorHandled(true);
                    handlePromotionCodeError({
                      errorType: reasonCodeParse.data,
                      data: parsed.data,
                      code: applicationFormData?.data.loan.code ?? undefined,
                      amount,
                    });
                    clearLoanProcessStore();

                    return;
                  }

                  // 422 without availableRevenueRate (NEXT_LOAN, FIRST_LOAN, USED, CODE_EXPIRED, CODE_NOT_YET_ENABLED, INACTIVE, INVALID_CODE)
                  const userId = merchantResponseData?.data.merchantData.user.userId;
                  const period = applicationFormData?.data.loan.period;
                  if (userId !== undefined && period !== undefined) {
                    deleteDiscountForPeriod(userId, period);
                  }
                  setDiscountErrorHandled(true);
                  handlePromotionCodeError({ errorType: reasonCodeParse.data });
                  clearLoanProcessStore();

                  return;
                }
              }
            }

            // 400/500 or unknown errors — global modal handles display
            clearDiscountStorage();
          },
        },
      );
    }
  }, [
    applicationFormData,
    isMutateRequestEnable,
    source,
    handlePromotionCodeError,
    clearLoanProcessStore,
  ]);

  if (isProviderBlocked) {
    return <BlockNewApplicationSubmit />;
  }

  if (addLoanStatus === 'pending' || (addLoanStatus === 'idle' && isMutateRequestEnable)) {
    return <ApplicationProcessing />;
  }

  if (isProviderRequestFailed(providerRequest)) {
    return <ApplicationSuspended />;
  }

  if (discountErrorHandled) {
    return null;
  }

  return <ApplicationSuspended />;
}
